import { Component, ChangeDetectionStrategy, signal, computed, inject, effect } from '@angular/core';

import { TradingModeService, HealthPollResult } from '@core/services/trading-mode.service';
import {
  describeUnassessedComponents,
  platformHealthVerdict,
  readUnassessedComponents,
} from '@core/services/platform-health-verdict';
import { TooltipDirective } from '@shared/components/ui/tooltip/tooltip.directive';
import { environment } from '@environments/environment';

export type ApiStatus = 'online' | 'offline' | 'degraded' | 'unknown' | 'checking';

/** Narrow an unknown poll error to the HTTP-ish fields the status mapping reads. */
function readHttpError(error: unknown): { status: number | null; statusText: string | null; name: string | null } {
  if (!error || typeof error !== 'object') {
    return { status: null, statusText: null, name: null };
  }
  const e = error as { status?: unknown; statusText?: unknown; name?: unknown };
  return {
    status: typeof e.status === 'number' ? e.status : null,
    statusText: typeof e.statusText === 'string' ? e.statusText : null,
    name: typeof e.name === 'string' ? e.name : null,
  };
}

/**
 * RF-QA-172 · The footer no longer runs its own `/api/v1/health` poller
 * (`timer(1000, 30000)` → a second request ~1.9 s after TradingModeService's
 * every 30 s). It now derives the "API Online" pill from the single shared
 * poll published as `TradingModeService.health`, which is also paused while
 * the tab is hidden (RF-QA-171).
 */
@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [TooltipDirective],
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FooterComponent {
  private readonly tradingMode = inject(TradingModeService);

  currentYear = new Date().getFullYear();
  readonly appVersion = environment.version;

  // API status tracking
  apiStatus = signal<ApiStatus>('checking');
  lastHealthCheckTime = signal<Date | null>(null);
  /**
   * F-FU-37 · Components the last successful `/api/v1/health` response listed as
   * not assessed. "API Online" is still shown for Healthy; this is the caveat.
   */
  readonly unassessedComponents = signal<readonly string[]>([]);

  /** Full caveat, e.g. `3 not assessed: API gateway, …` (tooltip + accessible description), or null. */
  readonly apiStatusCaveat = computed(() => describeUnassessedComponents(this.unassessedComponents()));

  /** Compact visible trigger text for the caveat, e.g. `3 not assessed`. */
  readonly apiStatusCaveatShort = computed(() => {
    const count = this.unassessedComponents().length;
    return count === 0 ? null : `${count} not assessed`;
  });

  // Computed display values
  apiStatusDisplay = computed(() => {
    switch (this.apiStatus()) {
      case 'online': return 'API Online';
      case 'offline': return 'API Offline';
      case 'degraded': return 'API Degraded';
      case 'unknown': return 'API Status Unknown';
      case 'checking': return 'Checking...';
    }
  });

  apiStatusClass = computed(() => {
    switch (this.apiStatus()) {
      case 'online': return 'text-bb-accent-green';
      case 'offline': return 'text-bb-accent-red';
      case 'degraded': return 'text-bb-accent-yellow';
      case 'unknown': return 'text-bb-text-secondary';
      case 'checking': return 'text-bb-text-muted';
    }
  });

  apiStatusDotClass = computed(() => {
    switch (this.apiStatus()) {
      case 'online': return 'bg-bb-accent-green';
      case 'offline': return 'bg-bb-accent-red';
      case 'degraded': return 'bg-bb-accent-yellow';
      case 'unknown': return 'bg-bb-text-muted';
      case 'checking': return 'bg-bb-text-muted animate-pulse';
    }
  });

  constructor() {
    // RF-QA-172: consume the shared poll instead of issuing a second request.
    effect(() => this.applyHealth(this.tradingMode.health()));
  }

  private applyHealth(result: HealthPollResult | null): void {
    if (result === null) {
      // No poll has settled yet.
      this.apiStatus.set('checking');
      this.unassessedComponents.set([]);
      return;
    }

    if (result.kind === 'ok') {
      // F-FU-37: LivenessResponse.status ("Healthy" | "Degraded" | "Unknown") goes
      // through the shared verdict mapping. "Unknown" (nothing assessed) and a
      // missing label are neutral, not the yellow "API Degraded".
      const verdict = platformHealthVerdict(result.response?.status);
      this.apiStatus.set(verdict === 'healthy' ? 'online' : verdict === 'unknown' ? 'unknown' : 'degraded');
      this.unassessedComponents.set(readUnassessedComponents(result.response));
      this.lastHealthCheckTime.set(result.at);
      return;
    }

    // A failed poll carries no component breakdown; do not keep an old caveat.
    this.unassessedComponents.set([]);

    const error = readHttpError(result.error);
    // RF-QA-172 · no "aborted request" branch any more: the shared poll cancels
    // superseded requests by unsubscribing (switchMap), which never surfaces as
    // an error. A status-0 "Unknown Error" is Angular's fetch backend reporting
    // a REAL network failure, which must read as offline, not be ignored.

    // Determine status based on error type
    if (error.status === 0) {
      // Network error or CORS - API is unreachable
      this.apiStatus.set('offline');
    } else if (error.status !== null && error.status >= 500) {
      // Server error. 503 (Service Unavailable) and 504 (Gateway Timeout)
      // are transient/degraded; other 5xx mean the API is unreachable.
      this.apiStatus.set(error.status === 503 || error.status === 504 ? 'degraded' : 'offline');
    } else if (error.status === 408) {
      // Request timeout - API is degraded
      this.apiStatus.set('degraded');
    } else {
      // Other errors (4xx, client-side timeout) - API is technically online but may have issues
      this.apiStatus.set('degraded');
    }
    this.lastHealthCheckTime.set(result.at);
  }
}
